<template>
  <div class="multidropdown" ref="root" :class="{ open, active: selected.length > 0 }">
    <button class="dd-summary" type="button" @click="toggle">
      <span class="dd-label">{{ summary }}</span>
      <span v-if="selected.length" class="dd-count">{{ selected.length }}</span>
      <span class="dd-chevron">{{ open ? '▲' : '▼' }}</span>
    </button>
    <div v-if="open" class="dd-menu">
      <input
        v-if="options.length > 12"
        class="dd-search"
        type="text"
        placeholder="Поиск…"
        :value="query"
        @input="onQuery"
      />
      <label class="dd-option dd-all" @click.prevent="clear">
        <input type="checkbox" :checked="allChecked" />
        <span>{{ allLabel }}</span>
      </label>
      <div class="dd-list">
        <template v-if="specialList.length">
          <label
            v-for="opt in specialList"
            :key="opt.value"
            class="dd-option dd-special"
            :title="opt.title"
            @click.prevent="toggleSpecial(opt)"
          >
            <input type="checkbox" :checked="selected.includes(opt.value)" />
            <span>{{ opt.label }}</span>
          </label>
          <div class="dd-divider"></div>
        </template>
        <template v-if="groups.length">
          <label
            v-for="group in groups"
            :key="group.label"
            class="dd-option dd-group"
            :title="`Выделить города группы ${group.label}`"
            @click.prevent="toggleGroup(group)"
          >
            <input
              type="checkbox"
              :checked="groupChecked(group)"
              :indeterminate.prop="groupIndeterminate(group)"
            />
            <span class="dd-group-label">{{ group.label }}</span>
            <span class="dd-group-count">{{ groupCount(group) }}/{{ groupValues(group).length }}</span>
          </label>
          <div class="dd-divider"></div>
        </template>
        <label v-for="opt in filteredOptions" :key="optValue(opt)" class="dd-option">
          <input type="checkbox" :checked="selected.includes(optValue(opt))" @change="toggleOption(opt)" />
          <span v-if="optColor(opt)" class="dd-dot" :style="{ background: optColor(opt) }"></span>
          <span :title="optLabel(opt)">{{ optLabel(opt) }}</span>
        </label>
        <div v-if="!filteredOptions.length" class="dd-empty">Нет вариантов</div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

export default {
  name: 'MultiDropdown',
  props: {
    label: { type: String, required: true },
    options: { type: Array, default: () => [] },
    modelValue: { type: Array, default: () => [] },
    allLabel: { type: String, default: 'Все' },
    // «Все» эмитит полный список значений вместо [] (режим «пусто = ничего не выбрано»)
    clearEmitsAll: { type: Boolean, default: false },
    // Спец-пункты над списком в отдельном блоке через разделители
    // (напр. «С владельцем» / «-- (без владельца)»); их value попадает в общий modelValue
    specialOptions: { type: Array, default: () => [] },
    // Устаревший вариант одного спец-пункта (совместимость)
    specialOption: { type: Object, default: null },
    // Быстрые группы-кнопки через разделитель: [{label, values: [...]}]
    groups: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const open = ref(false);
    const query = ref('');
    const root = ref(null);

    const selected = computed(() => props.modelValue || []);
    const optValue = (opt) => (opt !== null && typeof opt === 'object' ? opt.value : opt);
    const optLabel = (opt) => (opt !== null && typeof opt === 'object' ? opt.label : opt);
    const optColor = (opt) =>
      opt !== null && typeof opt === 'object' && opt.color ? opt.color : null;
    const filteredOptions = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return props.options;
      return props.options.filter((opt) => String(optLabel(opt)).toLowerCase().includes(q));
    });
    // clearEmitsAll: «Все» = только полный выбор (пусто = «ничего»);
    // обычный режим: «Все» = пустая выборка (пусто = «все»).
    const allChecked = computed(() =>
      props.clearEmitsAll
        ? props.options.length > 0 && selected.value.length === props.options.length
        : selected.value.length === 0
    );
    const summary = computed(() => {
      if (!selected.value.length) return props.label;
      if (selected.value.length === 1) {
        const match = props.options.find((opt) => optValue(opt) === selected.value[0]);
        return match !== undefined ? optLabel(match) : selected.value[0];
      }
      return `${props.label}: ${selected.value.length}`;
    });

    const toggle = () => {
      open.value = !open.value;
      if (!open.value) query.value = '';
    };
    const toggleOption = (opt) => {
      const value = optValue(opt);
      const next = selected.value.includes(value)
        ? selected.value.filter((v) => v !== value)
        : [...selected.value, value];
      emit('update:modelValue', next);
    };
    const clear = () => {
      if (props.clearEmitsAll && props.options.length) {
        emit('update:modelValue', props.options.map((opt) => optValue(opt)));
      } else {
        emit('update:modelValue', []);
      }
    };
    const specialList = computed(() => {
      if (props.specialOptions && props.specialOptions.length) return props.specialOptions;
      return props.specialOption ? [props.specialOption] : [];
    });
    const toggleSpecial = (opt) => {
      if (!opt) return;
      const value = opt.value;
      const next = selected.value.includes(value)
        ? selected.value.filter((v) => v !== value)
        : [...selected.value, value];
      emit('update:modelValue', next);
    };
    // Значения группы, реально присутствующие в опциях
    const groupValues = (group) => {
      const available = new Set(props.options.map((opt) => optValue(opt)));
      return (group.values || []).filter((v) => available.has(v));
    };
    const groupChecked = (group) => {
      const values = groupValues(group);
      return values.length > 0 && values.every((v) => selected.value.includes(v));
    };
    // Частичный выбор группы — чекбокс в состоянии indeterminate
    const groupIndeterminate = (group) => {
      const count = groupCount(group);
      return count > 0 && count < groupValues(group).length;
    };
    const groupCount = (group) => groupValues(group).filter((v) => selected.value.includes(v)).length;
    const toggleGroup = (group) => {
      const values = groupValues(group);
      if (!values.length) return;
      const allIn = values.every((v) => selected.value.includes(v));
      const next = allIn
        ? selected.value.filter((v) => !values.includes(v))
        : Array.from(new Set([...selected.value, ...values]));
      emit('update:modelValue', next);
    };
    const onQuery = (event) => { query.value = event.target.value; };

    const onDocClick = (event) => {
      if (open.value && root.value && !root.value.contains(event.target)) {
        open.value = false;
        query.value = '';
      }
    };

    onMounted(() => document.addEventListener('click', onDocClick));
    onBeforeUnmount(() => document.removeEventListener('click', onDocClick));

    return { open, query, root, selected, filteredOptions, allChecked, summary, toggle, toggleOption, clear, onQuery, optValue, optLabel, optColor, specialList, toggleSpecial, groupValues, groupChecked, groupIndeterminate, groupCount, toggleGroup };
  },
};
</script>

<style scoped>
.multidropdown {
  position: relative;
  min-width: 150px;
}

.dd-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 36px;
  padding: 4px 10px;
  box-sizing: border-box;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #344054;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
}

.multidropdown.open .dd-summary {
  border-color: #84adff;
}

.multidropdown.active .dd-summary {
  border-color: #84adff;
  box-shadow: 0 0 0 2px rgba(23, 92, 211, 0.12);
}

.dd-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dd-count {
  flex-shrink: 0;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eef4ff;
  color: #0056b3;
  font-size: 12px;
  font-weight: 600;
}

.dd-chevron {
  flex-shrink: 0;
  color: #98a2b3;
  font-size: 10px;
}

.dd-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 30;
  width: min(300px, 90vw);
  max-height: 320px;
  display: flex;
  flex-direction: column;
  padding: 6px;
  box-sizing: border-box;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.14);
}

.dd-search {
  width: 100%;
  margin-bottom: 6px;
  padding: 6px 8px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  font: inherit;
  font-size: 13px;
  box-sizing: border-box;
}

.dd-list {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dd-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: 6px;
  font-size: 14px;
  color: #344054;
  cursor: pointer;
  user-select: none;
}

.dd-option:hover {
  background: #f0f6ff;
}

.dd-option span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dd-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dd-all {
  color: #6b7280;
  border-bottom: 1px solid #eaecf0;
  border-radius: 6px 6px 0 0;
  margin-bottom: 4px;
}

.dd-divider {
  height: 1px;
  background: #eaecf0;
  margin: 4px 2px;
  flex-shrink: 0;
}

.dd-special {
  font-weight: 500;
}

.dd-group {
  background: none;
  border: none;
  font: inherit;
  gap: 8px;
  padding: 5px 6px;
}

.dd-group-label {
  flex: 1;
  text-align: left;
  color: #344054;
}

.dd-group-count {
  flex-shrink: 0;
  font-size: 11px;
  color: #98a2b3;
}

.dd-group.active {
  background: #eef4ff;
}

.dd-group.active .dd-group-count {
  color: #344054;
  font-weight: 600;
}

.dd-empty {
  padding: 8px 6px;
  color: #98a2b3;
  font-size: 13px;
}
</style>
